import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`The data warehouse can link to data in your object storage system like S3. To start, you'll need to:`}</p>
    <ol>
      <li parentName="ol">{`Create a bucket in S3`}</li>
      <li parentName="ol">{`Set up an access key and secret`}</li>
      <li parentName="ol">{`Add data to the bucket (we'll use Airbyte)`}</li>
      <li parentName="ol">{`Create the table in PostHog`}</li>
    </ol>
    <h2 {...{
      "id": "step-1-creating-a-bucket-in-s3"
    }}>{`Step 1: Creating a bucket in S3`}</h2>
    <ol>
      <li parentName="ol">{`Log in to `}<a parentName="li" {...{
          "href": "https://console.aws.amazon.com/"
        }}>{`AWS`}</a>{`.`}</li>
      <li parentName="ol">{`Open `}<a parentName="li" {...{
          "href": "https://s3.console.aws.amazon.com/"
        }}>{`S3`}</a>{` in the AWS console and create a new bucket. We suggest `}<inlineCode parentName="li">{`us-east-1`}</inlineCode>{` if you're using PostHog Cloud US, or `}<inlineCode parentName="li">{`eu-central-1`}</inlineCode>{` if you're using PostHog Cloud EU.`}</li>
    </ol>
    <p><img parentName="p" {...{
        "src": "https://res.cloudinary.com/dmukukwp6/image/upload/v1710055416/posthog.com/contents/images/docs/apps/s3-export/bucket.png",
        "alt": "created bucket"
      }}></img></p>
    <blockquote>
      <p parentName="blockquote">{`Make sure to note both the name and region of your bucket, we'll need these later.`}</p>
    </blockquote>
    <h2 {...{
      "id": "step-2-set-up-access-policy-and-key"
    }}>{`Step 2: Set up access policy and key`}</h2>
    <p>{`Next, we need to create a new user in our AWS console with programmatic access to our newly created bucket.`}</p>
    <ol>
      <li parentName="ol">{`Open `}<a parentName="li" {...{
          "href": "https://console.aws.amazon.com/iam/home"
        }}>{`IAM`}</a>{` and create a new policy to enable access to this bucket`}</li>
      <li parentName="ol">{`On the left under `}<strong parentName="li">{`Access management`}</strong>{`, select `}<strong parentName="li">{`Policies`}</strong>{` and click `}<strong parentName="li">{`Create policy`}</strong></li>
      <li parentName="ol">{`Under the service, choose `}<strong parentName="li">{`S3`}</strong></li>
      <li parentName="ol">{`Under `}<strong parentName="li">{`Actions`}</strong>{`, select:`}</li>
      <li parentName="ol"><strong parentName="li">{`List`}</strong>{` -> `}<strong parentName="li">{`ListBucket`}</strong>{` and `}<strong parentName="li">{`ListBucketMultipartUploads`}</strong></li>
      <li parentName="ol"><strong parentName="li">{`Read`}</strong>{` -> `}<strong parentName="li">{`GetBucketLocation`}</strong>{` and `}<strong parentName="li">{`GetObject`}</strong></li>
      <li parentName="ol"><strong parentName="li">{`Write`}</strong>{` -> `}<strong parentName="li">{`AbortMultipartUpload`}</strong>{` and `}<strong parentName="li">{`PutObject`}</strong></li>
      <li parentName="ol"><strong parentName="li">{`Permission Management`}</strong>{` -> `}<strong parentName="li">{`PutObjectAcl`}</strong></li>
      <li parentName="ol">{`Under `}<strong parentName="li">{`Resources`}</strong>{`, select `}<strong parentName="li">{`Specific`}</strong>{`. Under object, click `}<strong parentName="li">{`Add ARNs`}</strong></li>
      <li parentName="ol">{`Specify your bucket name and choose `}<strong parentName="li">{`Any object name`}</strong>{`. In the example below, replace `}<inlineCode parentName="li">{`posthog-s3-export`}</inlineCode>{` with the bucket name you chose in the previous section`}</li>
    </ol>
    <p><img parentName="p" {...{
        "src": "https://res.cloudinary.com/dmukukwp6/image/upload/Clean_Shot_2024_07_15_at_15_19_29_2x_15416e8e84.png",
        "alt": "bucket arn config"
      }}></img></p>
    <ol {...{
      "start": 7
    }}>
      <li parentName="ol">{`Your policy in JSON should look like this:`}</li>
    </ol>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "Version": "2012-10-17",
    "Statement": [
        {
            "Sid": "VisualEditor0",
            "Effect": "Allow",
            "Action": [
                "s3:PutObject",
                "s3:GetObject",
                "s3:PutObjectAcl",
                "s3:ListBucket",
                "s3:ListBucketMultipartUploads",
                "s3:AbortMultipartUpload",
                "s3:GetBucketLocation"
            ],
            "Resource": "arn:aws:s3:::posthog-s3-export/*"
        }
    ]
}
`}</code></pre>
    <ol {...{
      "start": 8
    }}>
      <li parentName="ol">{`Click "Next" until you end up on the "Review Policy" page`}</li>
      <li parentName="ol">{`Give your policy a name and click "Create policy"`}</li>
    </ol>
    <p>{`The final step is to create a new user and give them access to our bucket by attaching our newly created policy.`}</p>
    <ol>
      <li parentName="ol">{`Open `}<a parentName="li" {...{
          "href": "https://console.aws.amazon.com/iam/home"
        }}>{`IAM`}</a>{` and navigate to `}<strong parentName="li">{`Users`}</strong>{` on the left`}</li>
      <li parentName="ol">{`Click `}<strong parentName="li">{`Create user`}</strong>{`, add a user name, and click `}<strong parentName="li">{`Next`}</strong></li>
      <li parentName="ol">{`Select `}<strong parentName="li">{`Attach policies directly`}</strong></li>
      <li parentName="ol">{`Search for the policy you just created and click the checkbox on the far left to attach it to this user`}</li>
      <li parentName="ol">{`Click `}<strong parentName="li">{`Next`}</strong>{` and then click `}<strong parentName="li">{`Create user`}</strong></li>
    </ol>
    <p><img parentName="p" {...{
        "src": "https://res.cloudinary.com/dmukukwp6/image/upload/Clean_Shot_2024_07_15_at_16_16_34_2x_9f0f99d7a4.png",
        "alt": "User"
      }}></img></p>
    <ol {...{
      "start": 8
    }}>
      <li parentName="ol">{`Once created, search and click on the user name and then click `}<strong parentName="li">{`Create access key`}</strong></li>
      <li parentName="ol">{`Select `}<strong parentName="li">{`Application running outside AWS`}</strong>{` and then click `}<strong parentName="li">{`Next`}</strong></li>
      <li parentName="ol">{`Add a description tag value and click `}<strong parentName="li">{`Create access key`}</strong></li>
      <li parentName="ol">{`Copy the access key and secret access key values to somewhere safe. You will need to recreate these values if you lose them.`}</li>
    </ol>
    <p><img parentName="p" {...{
        "src": "https://res.cloudinary.com/dmukukwp6/image/upload/Clean_Shot_2024_07_15_at_16_17_45_2x_e7dcb9dd39.png",
        "alt": "AWS access keys"
      }}></img></p>
    <h2 {...{
      "id": "step-3-add-data-to-the-bucket"
    }}>{`Step 3: Add data to the bucket`}</h2>
    <blockquote>
      <p parentName="blockquote">{`For this section, we'll be using `}<strong parentName="p">{`Airbyte`}</strong>{`. However, we accept any data in `}<a parentName="p" {...{
          "href": "/tutorials/csv-query"
        }}>{`CSV`}</a>{` or Parquet format, so if you already have data in S3 you can skip this section.`}</p>
    </blockquote>
    <ol>
      <li parentName="ol">{`Go to `}<a parentName="li" {...{
          "href": "https://airbyte.com"
        }}>{`Airbyte`}</a>{` and sign up for an account if you haven't already.`}</li>
      <li parentName="ol">{`Go to connections and click "New connection"`}</li>
      <li parentName="ol">{`Select a source. For this example, we'll grab data from `}<strong parentName="li">{`Stripe`}</strong>{`, but you can use any of Airbyte's sources.`}</li>
      <li parentName="ol">{`Click "Set up a new destination"`}</li>
      <li parentName="ol">{`Select "S3" as the destination`}</li>
      <li parentName="ol">{`Fill in the "S3 Bucket Name", "S3 Bucket Region" with the name and region you created earlier.`}</li>
      <li parentName="ol">{`For "S3 Bucket Path", use `}<inlineCode parentName="li">{`airbyte`}</inlineCode>{`.`}</li>
      <li parentName="ol">{`For the "Output Format", pick Parquet. You can use the default settings`}</li>
      <li parentName="ol">{`Under "Optional fields", you'll want to add the access key and secret from step 2.`}</li>
      <li parentName="ol">{`In the next step, pick the streams you want to fill. Given you'll manually need to create a table for each stream, we suggest being selective.`}</li>
      <li parentName="ol">{`Wait for the sync to finish`}</li>
    </ol>
    <p><img parentName="p" {...{
        "src": "https://res.cloudinary.com/dmukukwp6/image/upload/v1710055416/posthog.com/contents/images/features/data-warehouse/airbyte-destination.png",
        "alt": "creating the s3 destination"
      }}></img></p>
    <h2 {...{
      "id": "step-4-create-the-table-in-posthog"
    }}>{`Step 4: Create the table in PostHog`}</h2>
    <ol>
      <li parentName="ol">{`Go to the `}<a parentName="li" {...{
          "href": "https://us.posthog.com/pipeline/sources"
        }}>{`Data pipeline page`}</a>{` and the sources tab in PostHog`}</li>
      <li parentName="ol">{`Click `}<strong parentName="li">{`New source`}</strong>{` and under self managed, look for `}<strong parentName="li">{`S3`}</strong>{` and click `}<strong parentName="li">{`Link`}</strong></li>
      <li parentName="ol">{`Fill in the table name.`}</li>
      <li parentName="ol">{`For the URL pattern, copy the URL from S3, up to the bucket name. Replace the file name with `}<inlineCode parentName="li">{`*`}</inlineCode>{`, as Airbyte will split large streams out into multiple files.`}
        <ul parentName="li">
          <li parentName="ul">{`For example: `}<inlineCode parentName="li">{`https://your-bucket.s3.amazonaws.com/airbyte/invoices/*`}</inlineCode></li>
        </ul>
      </li>
      <li parentName="ol">{`For file format, select Parquet. Fill in the access key and secret key.`}</li>
    </ol>
    <blockquote>
      <p parentName="blockquote">{`You'll want to repeat this step for each "stream" or folder that Airbyte created in your S3 bucket.`}</p>
    </blockquote>
    <p><img parentName="p" {...{
        "src": "https://res.cloudinary.com/dmukukwp6/image/upload/v1710055416/posthog.com/contents/images/features/data-warehouse/create-table.png",
        "alt": "creating table in posthog"
      }}></img></p>
    <h2 {...{
      "id": "step-5-query-the-table"
    }}>{`Step 5: Query the table.`}</h2>
    <p>{`Amazing! You can now `}<a parentName="p" {...{
        "href": "/docs/data-warehouse/query"
      }}>{`query`}</a>{` your new table.`}</p>
    <h2 {...{
      "id": "troubleshooting"
    }}>{`Troubleshooting`}</h2>
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Create table failed: Could not get columns`}</strong>{`: Check that your `}<strong parentName="p">{`Files URL pattern`}</strong>{` is correct and that your file format is correct. For example, make sure your columns don't have spaces and there aren't commas in cells in your `}<inlineCode parentName="p">{`.csv`}</inlineCode>{` file.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Create table failed: Access was denied when reading the provided file`}</strong>{`: Make sure your access policies are correct.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Create table failed: The provided file is not in Parquet format`}</strong>{`: Make sure you've selected the correct file format.`}</p>
      </li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      